import type { LucideIcon } from "lucide-react";
import {
  Building2,
  GraduationCap,
  Headset,
  HeartPulse,
  Home,
  Scale,
  Scissors,
  ShoppingBag,
  TrendingUp,
  UtensilsCrossed,
} from "lucide-react";

export type UseCaseMessageKind = "text" | "pdf" | "link";

export type UseCaseMessage = {
  side: "customer" | "agent";
  text: string;
  kind?: UseCaseMessageKind;
  fileName?: string;
  fileMeta?: string;
  linkUrl?: string;
};

export type UseCaseScenario = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  headerLabel: string;
  link: string | null;
  messages: UseCaseMessage[];
};

export const useCaseScenarios: UseCaseScenario[] = [
  {
    id: "vendas",
    title: "Times de vendas",
    subtitle: "Qualificação e fechamento",
    description:
      "Qualifique leads no CRM nativo, registre negociações e deixe o agente de IA conduzir conversas no WhatsApp enquanto sua equipe foca no fechamento.",
    icon: TrendingUp,
    accent: "bg-violet-600",
    headerLabel: "WhatsApp · Vendas",
    link: "/qualificacao-de-leads-no-whatsapp",
    messages: [
      { side: "customer", text: "Oi! Quero saber mais sobre o plano Plus para minha equipe." },
      { side: "agent", text: "Olá! Quantas pessoas atendem hoje e qual o volume de conversas por dia?" },
      { side: "customer", text: "Somos 4 vendedores, umas 80 conversas por dia no WhatsApp." },
      { side: "agent", text: "Ótimo perfil. O Plus inclui CRM, agente de IA e até 3 números conectados." },
      { side: "customer", text: "Tem integração com o funil que já usamos?" },
      { side: "agent", text: "Sim, tudo no CRM nativo da FabChat, com histórico e tags por lead." },
      { side: "customer", text: "E follow-up automático quando o lead some?" },
      { side: "agent", text: "Sim! Você define intervalo e quantas tentativas antes de encerrar." },
      { side: "customer", text: "Perfeito. Podemos agendar uma demo amanhã à tarde?" },
      { side: "agent", text: "Tenho 15h ou 17h. Qual horário prefere?" },
      { side: "customer", text: "15h funciona." },
      { side: "agent", text: "Agendado! Te envio o link do Meet e um lembrete 1h antes da call." },
      { side: "customer", text: "Combinado, obrigado!" },
    ],
  },
  {
    id: "suporte",
    title: "Suporte ao cliente",
    subtitle: "Resolução e transbordo",
    description:
      "Resolva dúvidas frequentes com o agente de IA e transfira para um atendente humano quando precisar, com todo o histórico da conversa.",
    icon: Headset,
    accent: "bg-sky-600",
    headerLabel: "WhatsApp · Suporte",
    link: "/whatsapp/atendimento-automatico",
    messages: [
      { side: "customer", text: "O painel de conversas trava quando abro um chat com muitas mensagens." },
      { side: "agent", text: "Entendi. Isso acontece no app web, no celular ou nos dois?" },
      { side: "customer", text: "Só no app web, no Chrome." },
      { side: "agent", text: "Obrigado! Qual o nome da empresa e o plano que vocês usam hoje?" },
      { side: "customer", text: "Agência Norte, plano Plus." },
      { side: "agent", text: "Você consegue reproduzir com um print da tela de erro?" },
      { side: "customer", text: "Mando agora. Aparece “carregando” e não abre as mensagens." },
      { side: "agent", text: "Recebi. Registrei o defeito com prioridade média no suporte." },
      { side: "agent", text: "Vou te passar para a Camila. Ela já tem todo o histórico desta conversa." },
      { side: "customer", text: "Perfeito, fico no aguardo." },
      { side: "agent", text: "Camila assume em instantes. Obrigado por reportar!" },
      { side: "customer", text: "Valeu pela agilidade." },
    ],
  },
  {
    id: "imobiliario",
    title: "Imobiliário",
    subtitle: "Visitas e follow-up",
    description:
      "Informe detalhes do imóvel, agende visitas com Google Calendar e mantenha o lead no funil com follow-up automático.",
    icon: Home,
    accent: "bg-teal-600",
    headerLabel: "Instagram · Imóveis",
    link: "/setores/imobiliarias",
    messages: [
      { side: "customer", text: "Oi! Vi o apê de 2 quartos no Batel. Ainda está disponível?" },
      { side: "agent", text: "Olá! Sim, está disponível. 72 m², 2 vagas, condomínio com lazer." },
      { side: "customer", text: "Qual o valor e a documentação?" },
      { side: "agent", text: "R$ 480 mil, documentação ok. Aceita financiamento pela Caixa." },
      { side: "customer", text: "O condomínio inclui o quê?" },
      { side: "agent", text: "Piscina, academia, salão de festas e portaria 24h." },
      { side: "customer", text: "Consigo visitar amanhã à tarde?" },
      { side: "agent", text: "Tenho 10h ou 15h. Qual horário prefere?" },
      { side: "customer", text: "15h funciona. Meu nome é Marina Costa." },
      { side: "agent", text: "Visita agendada, Marina! Endereço e lembrete enviados." },
      { side: "customer", text: "Preciso levar algum documento?" },
      { side: "agent", text: "RG e comprovante de renda, se for simular financiamento. Até amanhã às 15h!" },
    ],
  },
  {
    id: "clinicas",
    title: "Saúde e clínicas",
    subtitle: "Triagem e agenda",
    description:
      "Triagem inicial, dúvidas frequentes e agendamentos com Google Calendar, com lembretes automáticos para reduzir faltas.",
    icon: HeartPulse,
    accent: "bg-rose-500",
    headerLabel: "WhatsApp · Clínica",
    link: "/setores/clinicas",
    messages: [
      { side: "customer", text: "Boa tarde! Quero marcar avaliação com a Dra. Ana." },
      { side: "agent", text: "Olá! A Dra. Ana atende avaliações de 50 min. É sua primeira consulta?" },
      { side: "customer", text: "Sim, primeira vez." },
      { side: "agent", text: "Tenho amanhã às 10h ou sexta às 16h. Qual prefere?" },
      { side: "customer", text: "Sexta às 16h." },
      { side: "agent", text: "Confirmado. Me passa seu nome completo para a ficha?" },
      { side: "customer", text: "João Silva" },
      { side: "agent", text: "Obrigado, João. Convênio ou particular?" },
      { side: "customer", text: "Particular." },
      { side: "agent", text: "Agendado no Google Calendar. Lembrete na véspera e orientações de jejum, se precisar." },
      { side: "customer", text: "Tem estacionamento na clínica?" },
      { side: "agent", text: "Sim, 2h grátis no subsolo. Te enviei o mapa no link de confirmação." },
    ],
  },
  {
    id: "educacao",
    title: "Educação",
    subtitle: "Matrículas e dúvidas",
    description:
      "Tire dúvidas sobre cursos e matrículas pelo Instagram ou WhatsApp, com leads organizados no CRM da plataforma.",
    icon: GraduationCap,
    accent: "bg-indigo-600",
    headerLabel: "Instagram · Cursos",
    link: "/setores/educacao",
    messages: [
      { side: "customer", text: "Oi! O curso de marketing ainda tem vaga para março?" },
      { side: "agent", text: "Olá! Sim, ainda há vagas na turma que começa dia 10/03." },
      { side: "customer", text: "Qual a carga horária e o valor?" },
      { side: "agent", text: "40h ao vivo + materiais. R$ 890 à vista ou 6x de R$ 165." },
      { side: "customer", text: "As aulas ficam gravadas?" },
      { side: "agent", text: "Sim, por 6 meses na área do aluno, para revisar quando quiser." },
      { side: "customer", text: "É online ou presencial?" },
      { side: "agent", text: "100% online ao vivo, com encontros às terças e quintas, 19h." },
      { side: "customer", text: "Quero me matricular." },
      { side: "agent", text: "Ótimo! Encaminhei à secretaria. O link de pagamento chega em poucos minutos." },
      { side: "customer", text: "Recebo certificado?" },
      { side: "agent", text: "Sim, certificado digital após concluir as atividades. Bem-vinda à turma!" },
    ],
  },
  {
    id: "saas",
    title: "SaaS e serviços",
    subtitle: "Onboarding e retenção",
    description:
      "Atenda dúvidas de onboarding e suporte no WhatsApp, com leads e histórico no CRM nativo da FabChat, sem depender de ferramentas externas.",
    icon: Building2,
    accent: "bg-slate-700",
    headerLabel: "WhatsApp · SaaS",
    link: "/setores/servicos",
    messages: [
      { side: "customer", text: "Acabei de criar a conta. Como organizo meus leads?" },
      { side: "agent", text: "Bem-vindo! No CRM você cria estágios, tags e vê o histórico de cada conversa." },
      { side: "customer", text: "Consigo importar contatos de uma planilha?" },
      { side: "agent", text: "Sim, pela área de contatos em CSV. Quer o passo a passo?" },
      { side: "customer", text: "Sim, e também gostaria de uma demo ao vivo." },
      { side: "agent", text: "Enviei o guia em PDF. Para demo: amanhã 14h ou quinta 10h?" },
      { side: "customer", text: "Quinta às 10h." },
      { side: "agent", text: "Demo agendada! Convite no e-mail e lembrete na quinta de manhã." },
      { side: "customer", text: "Quantos usuários entram no plano inicial?" },
      { side: "agent", text: "Até 5 membros no workspace, com permissões por função." },
      { side: "customer", text: "Perfeito, vou testar hoje." },
      { side: "agent", text: "Qualquer dúvida no onboarding, estou por aqui. Boa configuração!" },
    ],
  },
  {
    id: "ecommerce",
    title: "E-commerce",
    subtitle: "Catálogo e vendas",
    description:
      "Responda sobre produtos, preços e disponibilidade com o catálogo cadastrado, direto na conversa.",
    icon: ShoppingBag,
    accent: "bg-amber-600",
    headerLabel: "WhatsApp · Loja",
    link: null,
    messages: [
      { side: "customer", text: "Oi! Vocês têm o tênis Runner em tamanho 42?" },
      { side: "agent", text: "Olá! Sim, temos no catálogo: preto e branco, numeração 42." },
      { side: "customer", text: "Qual o preço do preto?" },
      { side: "agent", text: "R$ 329,90. Frete grátis acima de R$ 299 para sua região." },
      { side: "customer", text: "Prazo de entrega para Curitiba?" },
      { side: "agent", text: "3 a 5 dias úteis após confirmação do pagamento." },
      { side: "customer", text: "Pode me mandar o link?" },
      {
        side: "agent",
        text: "Segue o link do tênis Runner:",
        kind: "link",
        fileName: "Tênis Runner · Preto 42",
        linkUrl: "loja.fabchat.demo/runner",
      },
      { side: "agent", text: "Quer adicionar meia esportiva com 15% off?" },
      { side: "customer", text: "Boa! Vou levar o tênis e a meia." },
      { side: "agent", text: "Perfeito! Carrinho montado com o desconto aplicado." },
      { side: "customer", text: "Aceita Pix?" },
      { side: "agent", text: "Sim! No checkout você escolhe Pix, cartão ou boleto. Qualquer dúvida, me chama." },
    ],
  },
  {
    id: "beleza",
    title: "Beleza e estética",
    subtitle: "Procedimentos e agenda",
    description:
      "Tire dúvidas sobre procedimentos e valores pelo Instagram, agende horários e deixe o agente cuidar do primeiro contato.",
    icon: Scissors,
    accent: "bg-fuchsia-600",
    headerLabel: "Instagram · Estética",
    link: null,
    messages: [
      { side: "customer", text: "Oi! Vi o Reels da limpeza de pele. Quanto fica?" },
      { side: "agent", text: "Olá! A limpeza de pele está R$ 180, sessão de 50 min." },
      { side: "customer", text: "Inclui extração e máscara?" },
      { side: "agent", text: "Sim: higienização, extração, máscara calmante e protetor no final." },
      { side: "customer", text: "Serve para pele sensível?" },
      { side: "agent", text: "Sim, usamos produtos hipoalergênicos. A avaliação confirma o protocolo." },
      { side: "customer", text: "Tem horário sexta à tarde?" },
      { side: "agent", text: "Sexta às 14h ou 15h30. Qual prefere?" },
      { side: "customer", text: "15h30 está ótimo." },
      { side: "agent", text: "Reservado! Te enviei confirmação e orientações." },
      { side: "customer", text: "Preciso evitar sol antes?" },
      { side: "agent", text: "Sim, evite sol forte 48h antes e depois. Até sexta às 15h30!" },
    ],
  },
  {
    id: "restaurantes",
    title: "Restaurantes",
    subtitle: "Reservas e atendimento",
    description:
      "Confirme reservas e responda sobre cardápio e horários no WhatsApp, sem perder mensagens no horário de pico.",
    icon: UtensilsCrossed,
    accent: "bg-red-600",
    headerLabel: "WhatsApp · Restaurante",
    link: null,
    messages: [
      { side: "customer", text: "Boa noite! Mesa para 4 pessoas hoje às 20h?" },
      { side: "agent", text: "Boa noite! Temos mesa no salão às 20h ou 20h30." },
      { side: "customer", text: "20h está ótimo. Tem opção no terraço?" },
      { side: "agent", text: "O terraço está reservado hoje, mas o salão tem vista legal. Serve?" },
      { side: "customer", text: "Serve sim. Em nome de Carlos Mendes." },
      { side: "agent", text: "Reserva confirmada: 20h, 4 pessoas, Carlos Mendes." },
      { side: "customer", text: "Vocês têm opção vegana no cardápio?" },
      {
        side: "agent",
        text: "Sim! Segue o cardápio em PDF:",
        kind: "pdf",
        fileName: "Cardapio_Vegano.pdf",
        fileMeta: "1,2 MB",
      },
      { side: "customer", text: "Tem estacionamento?" },
      { side: "agent", text: "Sim, manobrista no subsolo. R$ 25 a noite." },
      { side: "customer", text: "Alguma criança no grupo, tem cadeirão?" },
      { side: "agent", text: "Temos sim! Anotei na reserva. Esperamos vocês às 20h!" },
    ],
  },
  {
    id: "juridico",
    title: "Advocacia",
    subtitle: "Triagem e consultas",
    description:
      "Colete informações iniciais, registre o lead no CRM e agende consultas com Google Calendar para a equipe jurídica.",
    icon: Scale,
    accent: "bg-stone-600",
    headerLabel: "WhatsApp · Escritório",
    link: null,
    messages: [
      { side: "customer", text: "Boa tarde. Preciso de orientação sobre rescisão trabalhista." },
      { side: "agent", text: "Boa tarde. Vou te ajudar na triagem. Há quanto tempo saiu da empresa?" },
      { side: "customer", text: "Há 2 semanas. Não assinei acordo ainda." },
      { side: "agent", text: "Entendido. Você recebeu alguma verba ou apenas o aviso?" },
      { side: "customer", text: "Só o aviso prévio. Nada na conta ainda." },
      { side: "agent", text: "Há verbas a conferir: rescisão, férias e FGTS." },
      { side: "customer", text: "Trabalhei 3 anos na mesma função." },
      { side: "agent", text: "Isso ajuda no cálculo. Recomendo consulta presencial com documentos." },
      { side: "customer", text: "Pode agendar com o Dr. Marcos?" },
      { side: "agent", text: "Consulta agendada: quinta, 14h. Enviei confirmação e lista de documentos." },
      { side: "customer", text: "Qual o valor da consulta?" },
      { side: "agent", text: "R$ 280 a primeira consulta. Na quinta o Dr. Marcos te explica os próximos passos." },
    ],
  },
];
